import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { useQueryClient } from '@tanstack/react-query';
import { storage, functions } from '../firebase';
import { resourcesQueryKeys } from '../hooks/useResourcesData';
import {
  getAudioDuration,
  getFileTypeFromMimeType,
  formatDuration,
} from '../components/ResourcesTab/ResourcesDataHandlers';
import { formatFileSize } from '../utils/fileFormatting';
import { GLOBAL_ALLOWED_FILE_TYPES, GLOBAL_ALLOWED_EXTENSIONS } from '../constants/fileTypes';
import { useToast } from './ToastContext';
import UploadQueuePanel from '../components/ui_elements/UploadQueuePanel';

export interface UploadFile {
  id: string;
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'processing' | 'completed' | 'error' | 'paused';
  error?: string;
  downloadUrl?: string;
  resourceId?: string;
  /** Client-generated idempotency key sent to addResource so retries can never
   *  create duplicate resource documents. */
  pendingResourceId: string;
  showId: string;
  subcategory: string;
  parentCategory?: string;
  uploadTask?: any;
  isPausedByUser?: boolean;
  storagePath?: string;
  retryCount?: number;
  duration?: number;
}

export interface UploadTarget {
  showId: string;
  subcategory: string;
  parentCategory?: string;
}

interface UploadQueueContextValue {
  uploads: UploadFile[];
  enqueueFiles: (files: FileList | File[], target: UploadTarget) => void;
  retryUpload: (uploadId: string) => void;
  removeUpload: (uploadId: string) => void;
  pauseUpload: (uploadId: string) => void;
  resumeUpload: (uploadId: string) => void;
  pauseAll: () => void;
  clearQueue: () => void;
  /** After a folder rename: re-points every unfinished upload filed under
   *  `oldPath` (or a folder inside it) to `newPath`. Returns how many moved.
   *  The server-side rename only rewrites resources that already exist, so
   *  without this an upload still in flight registers under the old name and
   *  resurrects it as a file-derived folder with no record to delete. */
  retargetFolder: (showId: string, oldPath: string, newPath: string) => number;
}

const MAX_VIDEO_SIZE = 2 * 1024 * 1024 * 1024; // 2GB for videos (compressed server-side; matches storage.rules)
const MAX_NON_VIDEO_SIZE = 100 * 1024 * 1024; // 100MB for other files
const MAX_CONCURRENT_UPLOADS = 3;
const MAX_QUEUE_SIZE = 50;
const PROGRESS_THROTTLE_PERCENT = 2;
const AUTO_RETRY_MAX = 3;
const AUTO_RETRY_BASE_DELAY_MS = 1000;
// Outer bound on the metadata read that sits between the Storage upload and
// addResource. Longer than the helper's own timeout - it only fires if the
// helper itself fails to settle.
const DURATION_READ_TIMEOUT_MS = 20000;

/** Resolves to null if `promise` has not settled within `ms`. */
export const withTimeout = <T,>(promise: Promise<T>, ms: number): Promise<T | null> =>
  new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), ms);
    promise.then(
      value => { clearTimeout(timer); resolve(value); },
      () => { clearTimeout(timer); resolve(null); }
    );
  });

const generateUploadId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
const generateResourceId = () => `res${Date.now()}${Math.random().toString(36).slice(2, 10)}`;

export const validateFile = (file: File): string | null => {
  const fileExtension = file.name.split('.').pop()?.toLowerCase();
  const isVideo = file.type.startsWith('video/');

  if (!fileExtension || !GLOBAL_ALLOWED_EXTENSIONS.includes(fileExtension.toUpperCase())) {
    return `File type .${fileExtension} is not allowed. Allowed types: ${GLOBAL_ALLOWED_FILE_TYPES.join(', ')}`;
  }

  const maxSize = isVideo ? MAX_VIDEO_SIZE : MAX_NON_VIDEO_SIZE;
  if (file.size > maxSize) {
    const maxSizeLabel = isVideo ? '2 GB' : `${Math.round(maxSize / (1024 * 1024))} MB`;
    return `${isVideo ? 'Video' : 'File'} size must be less than ${maxSizeLabel}`;
  }

  return null;
};

// Derive resource kind from file MIME type
export const getResourceInfo = (file: File) => {
  const mime = file.type.toLowerCase();
  let kind: string;
  if (mime.startsWith('audio/')) kind = 'audio_original';
  else if (mime.startsWith('video/')) kind = 'rehearsal_video';
  else kind = 'sheet_music'; // PDF/doc/image default

  let category: string;
  if (kind.startsWith('audio') || kind === 'warmup') category = 'singing';
  else if (kind.includes('video') || kind === 'dance_chart') category = 'dancing';
  else category = 'acting';

  return { category, kind };
};

const UploadQueueContext = createContext<UploadQueueContextValue | null>(null);

export const UploadQueueProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [uploads, setUploads] = useState<UploadFile[]>([]);
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const uploadsRef = useRef<UploadFile[]>(uploads);
  const startedIdsRef = useRef<Set<string>>(new Set());
  const lastReportedProgressRef = useRef<Map<string, number>>(new Map());
  // Files that completed since the last time the queue drained, grouped by
  // show. Used for the drain-time invalidation + verification pass.
  const drainBatchRef = useRef<Map<string, { resourceId: string; name: string }[]>>(new Map());
  const drainInFlightRef = useRef(false);

  useEffect(() => { uploadsRef.current = uploads; }, [uploads]);

  const pendingCount = useMemo(() => uploads.filter(u => u.status === 'pending').length, [uploads]);
  const activeCount = useMemo(() => uploads.filter(u => u.status === 'uploading').length, [uploads]);
  const hasActiveWork = useMemo(
    () => uploads.some(u => ['pending', 'uploading', 'processing'].includes(u.status)),
    [uploads]
  );

  // Warn before the page unloads while uploads are in flight - closing the tab
  // between the Storage upload and the addResource call would strand the file.
  useEffect(() => {
    if (!hasActiveWork) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasActiveWork]);

  // Upload file bytes to Firebase Storage
  const uploadToStorage = useCallback(async (upload: UploadFile): Promise<{ downloadUrl: string; storagePath: string }> => {
    return new Promise((resolve, reject) => {
      // Reuse existing storagePath on retry, otherwise generate a new one
      const storagePath = upload.storagePath || (() => {
        const timestamp = Date.now();
        const sanitizedFileName = upload.file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const categoryPath = upload.parentCategory || 'resources';
        // Root uploads (empty subcategory) sit directly under the category
        // segment - keeping the empty segment would bake a double slash into
        // the object path. Same rule as the mobile queue.
        const folderSegment = upload.subcategory ? `${upload.subcategory}/` : '';
        return `shows/${upload.showId}/resources/${categoryPath}/${folderSegment}${timestamp}_${sanitizedFileName}`;
      })();

      const storageRef = ref(storage, storagePath);

      const metadata = {
        contentType: upload.file.type,
        customMetadata: {
          originalFileName: upload.file.name,
          uploadedAt: new Date().toISOString(),
          uploadedBy: 'mt-portal',
          subcategory: upload.subcategory,
          parentCategory: upload.parentCategory || '',
        },
      };

      const uploadTask = uploadBytesResumable(storageRef, upload.file, metadata);

      setUploads(prev =>
        prev.map(u => (u.id === upload.id ? { ...u, uploadTask, storagePath } : u))
      );

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);

          const lastReported = lastReportedProgressRef.current.get(upload.id) ?? 0;
          if (progress < 100 && progress - lastReported < PROGRESS_THROTTLE_PERCENT) {
            return;
          }
          lastReportedProgressRef.current.set(upload.id, progress);

          setUploads(prev =>
            prev.map(u => {
              if (u.id === upload.id) {
                if (u.isPausedByUser) {
                  return { ...u, progress };
                }
                return { ...u, progress, status: 'uploading' as const };
              }
              return u;
            })
          );
        },
        (error) => {
          console.error('Upload error:', error);
          let errorMessage = 'Upload failed';

          switch (error.code) {
            case 'storage/unauthorized':
              errorMessage = 'Permission denied';
              break;
            case 'storage/canceled':
              errorMessage = 'Upload canceled';
              break;
            case 'storage/quota-exceeded':
              errorMessage = 'Storage quota exceeded';
              break;
            case 'storage/retry-limit-exceeded':
              errorMessage = 'Upload timeout';
              break;
            default:
              errorMessage = error.message || 'Upload failed';
          }

          setUploads(prev =>
            prev.map(u =>
              u.id === upload.id ? { ...u, status: 'error' as const, error: errorMessage } : u
            )
          );
          // Reject with the readable message: processUpload writes the
          // rejection's message into the row, so rejecting with the raw
          // Storage error replaced this copy with SDK text. The code is kept
          // for the transient/permanent retry decision.
          reject(Object.assign(new Error(errorMessage), { code: error.code }));
        },
        async () => {
          try {
            const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
            setUploads(prev =>
              prev.map(u =>
                u.id === upload.id
                  ? { ...u, downloadUrl, status: 'processing' as const, progress: 100 }
                  : u
              )
            );
            resolve({ downloadUrl, storagePath });
          } catch (error) {
            setUploads(prev =>
              prev.map(u =>
                u.id === upload.id
                  ? { ...u, status: 'error' as const, error: 'Failed to get download URL' }
                  : u
              )
            );
            reject(Object.assign(new Error('Failed to get download URL'), { code: (error as any)?.code }));
          }
        }
      );
    });
  }, []);

  // Save resource metadata to Firestore via the addResource callable
  const saveResourceToDatabase = useCallback(async (
    upload: UploadFile,
    actualStoragePath: string,
    duration: number | undefined
  ) => {
    const addResource = httpsCallable(functions, 'addResource');
    const resourceInfo = getResourceInfo(upload.file);
    // The `upload` argument is the snapshot the worker started with. A folder
    // rename since then (retargetFolder) lives only in state, so the path is
    // re-read here, at the one moment it is written to the resource.
    const subcategory =
      uploadsRef.current.find(u => u.id === upload.id)?.subcategory ?? upload.subcategory;

    try {
      const result = await addResource({
        showId: upload.showId,
        category: resourceInfo.category,
        subcategory,
        kind: resourceInfo.kind,
        label: upload.file.name.replace(/\.[^/.]+$/, ''),
        storagePath: actualStoragePath,
        description: `Uploaded to ${subcategory || 'Resources'}`,
        fileSize: upload.file.size,
        mimeType: upload.file.type,
        duration,
        isPublic: false,
        // Idempotency key: the server reuses this ID, so a retry after a lost
        // response returns the existing document instead of creating a twin.
        clientResourceId: upload.pendingResourceId,
      });

      return result.data as {
        resourceId: string;
        downloadUrl?: string | null;
        urlGeneratedAt?: string | null;
      };
    } catch (error: any) {
      console.error('Error saving resource:', error);
      throw new Error(error.message || 'Failed to save resource to database');
    }
  }, []);

  // Insert a freshly created resource straight into the React Query cache so
  // it appears in the UI immediately, without waiting for a refetch (and
  // without risking the refetch being rate-limited or racing the write).
  const insertResourceIntoCache = useCallback((
    upload: UploadFile,
    resourceId: string,
    downloadUrl: string | undefined,
  ) => {
    // The files query holds the folder -> resources map directly.
    const key = resourcesQueryKeys.files(upload.showId);
    queryClient.setQueryData(key, (old: any) => {
      if (!old) return old;

      const folderKey = upload.subcategory;
      const existing: any[] = old[folderKey] || [];
      if (existing.some(f => f.resourceId === resourceId)) return old;

      const now = new Date();
      const formatted = {
        name: upload.file.name.replace(/\.[^/.]+$/, '') || 'Untitled',
        type: getFileTypeFromMimeType(upload.file.type),
        size: formatFileSize(upload.file.size),
        // Raw bytes, matching fetchShowResources.
        fileSize: upload.file.size,
        duration: upload.duration ? formatDuration(upload.duration) : undefined,
        compressionStatus: null,
        compressionStartedAt: null,
        compressionCompletedAt: null,
        compressionQuality: null,
        originalSizeMB: null,
        compressedSizeMB: null,
        compressionRatio: null,
        thumbnailStatus: null,
        thumbnailUrls: null,
        thumbnailGeneratedAt: null,
        uploadedAt: now,
        updatedAt: now,
        storagePath: upload.storagePath,
        downloadUrl: downloadUrl || null,
        resourceId,
        mimeType: upload.file.type,
        subcategory: folderKey,
        folderPath: undefined,
      };

      return {
        ...old,
        [folderKey]: [formatted, ...existing],
      };
    });
  }, [queryClient]);

  // Process a single upload. Retries recursively with backoff for transient
  // errors, keeping its slot in startedIdsRef the whole time so the queue
  // effect neither double-starts it nor strands it.
  const processUpload = useCallback(async (upload: UploadFile): Promise<void> => {
    try {
      setUploads(prev =>
        prev.map(u => (u.id === upload.id ? { ...u, status: 'uploading' } : u))
      );

      const { downloadUrl, storagePath } = await uploadToStorage(upload);

      // Extract audio duration before saving so both the server document and
      // the cache insert carry it.
      //
      // The bytes are already in Storage by this point, so nothing here may be
      // allowed to hold up addResource - a hang would strand the file with no
      // resource document and leave the row stuck on "Processing" for good.
      // getAudioDuration bounds itself; this is the queue's own guarantee.
      let duration: number | undefined;
      if (upload.file.type.startsWith('audio/')) {
        try {
          duration = (await withTimeout(getAudioDuration(upload.file), DURATION_READ_TIMEOUT_MS)) || undefined;
        } catch {
          duration = undefined;
        }
      }

      const resource = await saveResourceToDatabase({ ...upload, storagePath }, storagePath, duration);
      const finalDownloadUrl = resource.downloadUrl || downloadUrl;

      setUploads(prev =>
        prev.map(u =>
          u.id === upload.id
            ? {
                ...u,
                status: 'completed',
                downloadUrl: finalDownloadUrl,
                resourceId: resource.resourceId,
                storagePath,
                duration,
                progress: 100,
              }
            : u
        )
      );

      insertResourceIntoCache({ ...upload, storagePath, duration }, resource.resourceId, finalDownloadUrl);

      const batch = drainBatchRef.current.get(upload.showId) || [];
      batch.push({ resourceId: resource.resourceId, name: upload.file.name });
      drainBatchRef.current.set(upload.showId, batch);

      startedIdsRef.current.delete(upload.id);
      lastReportedProgressRef.current.delete(upload.id);
    } catch (error: any) {
      lastReportedProgressRef.current.delete(upload.id);

      const currentRetryCount = upload.retryCount ?? 0;
      const isTransient = !error.code?.includes('unauthorized') && !error.code?.includes('canceled');

      if (currentRetryCount < AUTO_RETRY_MAX && isTransient) {
        const delay = AUTO_RETRY_BASE_DELAY_MS * Math.pow(2, currentRetryCount);

        setUploads(prev =>
          prev.map(u =>
            u.id === upload.id
              ? { ...u, status: 'pending' as const, retryCount: currentRetryCount + 1, error: undefined }
              : u
          )
        );

        await new Promise(r => setTimeout(r, delay));

        // Re-check state after the backoff: the user may have removed or
        // manually retried the upload in the meantime.
        const current = uploadsRef.current.find(u => u.id === upload.id);
        if (!current || current.status !== 'pending' || !startedIdsRef.current.has(upload.id)) {
          startedIdsRef.current.delete(upload.id);
          return;
        }

        // Re-run directly rather than waiting for the queue effect - state
        // has not changed during the backoff, so the effect will not fire.
        return processUpload(current);
      }

      startedIdsRef.current.delete(upload.id);
      setUploads(prev =>
        prev.map(u =>
          u.id === upload.id
            ? { ...u, status: 'error', error: error.message || 'Upload failed' }
            : u
        )
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadToStorage, saveResourceToDatabase, insertResourceIntoCache]);

  // Start pending uploads while slots are available
  useEffect(() => {
    const current = uploadsRef.current;
    const pending = current.filter(u => u.status === 'pending');
    const currentActiveCount = current.filter(u => u.status === 'uploading').length;
    const slotsAvailable = MAX_CONCURRENT_UPLOADS - currentActiveCount;

    if (slotsAvailable <= 0 || pending.length === 0) return;

    const toStart = pending
      .filter(u => !startedIdsRef.current.has(u.id))
      .slice(0, slotsAvailable);

    toStart.forEach(upload => {
      startedIdsRef.current.add(upload.id);
      processUpload(upload);
    });
  }, [pendingCount, activeCount, processUpload]);

  // Drain-time pass: when the queue empties of active work, run ONE
  // invalidation per affected show, then verify every completed file is
  // actually visible in the cached list and tell the user either way.
  useEffect(() => {
    if (hasActiveWork || drainInFlightRef.current) return;
    if (drainBatchRef.current.size === 0) return;

    const batches = new Map(drainBatchRef.current);
    drainBatchRef.current = new Map();
    drainInFlightRef.current = true;

    (async () => {
      for (const [showId, items] of Array.from(batches.entries())) {
        try {
          await queryClient.invalidateQueries({ queryKey: resourcesQueryKeys.byShow(showId) });
        } catch (error) {
          console.error('Post-upload refetch failed:', error);
        }

        const data: any = queryClient.getQueryData(resourcesQueryKeys.files(showId));
        if (data) {
          const visibleIds = new Set(
            (Object.values(data) as any[][])
              .flat()
              .map((f: any) => f.resourceId)
          );
          const missing = items.filter(i => !visibleIds.has(i.resourceId));
          if (missing.length > 0) {
            const names = missing.slice(0, 3).map(m => m.name).join(', ');
            showToast(
              `${missing.length} uploaded file(s) are not showing yet (${names}${missing.length > 3 ? ', ...' : ''}). Refresh the page to see them.`,
              'warning',
              10000
            );
          } else {
            showToast(
              `${items.length} ${items.length === 1 ? 'file' : 'files'} uploaded successfully`,
              'success'
            );
          }
        } else {
          showToast(
            `${items.length} ${items.length === 1 ? 'file' : 'files'} uploaded successfully`,
            'success'
          );
        }
      }
      drainInFlightRef.current = false;
    })();
  }, [hasActiveWork, queryClient, showToast]);

  // Add files to the queue with validation, duplicate detection and cap
  const enqueueFiles = useCallback((files: FileList | File[], target: UploadTarget) => {
    setUploads(prev => {
      // Keep only entries that still need attention
      const active = prev.filter(u => ['uploading', 'processing', 'pending', 'paused'].includes(u.status));

      const existingKeys = new Set(
        active.map(u => `${u.file.name}_${u.file.size}_${u.file.lastModified}`)
      );

      const availableSlots = MAX_QUEUE_SIZE - active.length;
      const incoming = Array.from(files);
      const newUploads: UploadFile[] = [];
      let skippedDuplicates = 0;
      let skippedOverLimit = 0;

      for (const file of incoming) {
        const fileKey = `${file.name}_${file.size}_${file.lastModified}`;

        if (existingKeys.has(fileKey)) {
          skippedDuplicates++;
          continue;
        }

        const validationError = validateFile(file);

        if (validationError) {
          newUploads.push({
            id: generateUploadId(),
            pendingResourceId: generateResourceId(),
            file,
            progress: 0,
            status: 'error',
            error: validationError,
            showId: target.showId,
            subcategory: target.subcategory,
            parentCategory: target.parentCategory,
          });
          existingKeys.add(fileKey);
          continue;
        }

        const pendingValid = newUploads.filter(u => u.status !== 'error').length;
        if (pendingValid >= availableSlots) {
          skippedOverLimit++;
          continue;
        }

        newUploads.push({
          id: generateUploadId(),
          pendingResourceId: generateResourceId(),
          file,
          progress: 0,
          status: 'pending',
          showId: target.showId,
          subcategory: target.subcategory,
          parentCategory: target.parentCategory,
        });
        existingKeys.add(fileKey);
      }

      if (skippedOverLimit > 0) {
        newUploads.push({
          id: `${Date.now()}_limit_notice`,
          pendingResourceId: generateResourceId(),
          file: incoming[0],
          progress: 0,
          status: 'error',
          error: `Queue limit reached. ${skippedOverLimit} file(s) were not added. Maximum ${MAX_QUEUE_SIZE} files at once.`,
          showId: target.showId,
          subcategory: target.subcategory,
          parentCategory: target.parentCategory,
        });
      }

      if (skippedDuplicates > 0) {
        newUploads.push({
          id: `${Date.now()}_duplicate_notice`,
          pendingResourceId: generateResourceId(),
          file: incoming[0],
          progress: 0,
          status: 'error',
          error: `${skippedDuplicates} duplicate file(s) were skipped.`,
          showId: target.showId,
          subcategory: target.subcategory,
          parentCategory: target.parentCategory,
        });
      }

      return [...active, ...newUploads];
    });
  }, []);

  const retryUpload = useCallback((uploadId: string) => {
    startedIdsRef.current.delete(uploadId);
    setUploads(prev =>
      prev.map(u =>
        u.id === uploadId
          ? { ...u, status: 'pending' as const, error: undefined, progress: 0, retryCount: 0 }
          : u
      )
    );
  }, []);

  const removeUpload = useCallback((uploadId: string) => {
    startedIdsRef.current.delete(uploadId);
    setUploads(prev => {
      const upload = prev.find(u => u.id === uploadId);
      if (upload?.uploadTask && upload.status === 'uploading') {
        upload.uploadTask.cancel();
      }
      return prev.filter(u => u.id !== uploadId);
    });
  }, []);

  const pauseUpload = useCallback((uploadId: string) => {
    setUploads(prev => {
      const upload = prev.find(u => u.id === uploadId);
      if (upload?.uploadTask && upload.status === 'uploading') {
        try {
          upload.uploadTask.pause();
          return prev.map(u =>
            u.id === uploadId ? { ...u, status: 'paused' as const, isPausedByUser: true } : u
          );
        } catch (error) {
          console.error('Error pausing upload:', error);
          return prev;
        }
      }
      return prev;
    });
  }, []);

  const resumeUpload = useCallback((uploadId: string) => {
    setUploads(prev => {
      const upload = prev.find(u => u.id === uploadId);
      if (upload?.uploadTask && upload.status === 'paused') {
        try {
          upload.uploadTask.resume();
          return prev.map(u =>
            u.id === uploadId ? { ...u, status: 'uploading' as const, isPausedByUser: false } : u
          );
        } catch (error) {
          console.error('Error resuming upload:', error);
          return prev;
        }
      }
      return prev;
    });
  }, []);

  const pauseAll = useCallback(() => {
    setUploads(prev => {
      prev.filter(u => u.status === 'uploading' && u.uploadTask).forEach(u => {
        try { u.uploadTask.pause(); } catch {}
      });
      return prev.map(u =>
        u.status === 'uploading' ? { ...u, status: 'paused' as const, isPausedByUser: true } : u
      );
    });
  }, []);

  // Clears finished entries from the panel. Active uploads are deliberately
  // left running - closing the panel must never lose work.
  const clearQueue = useCallback(() => {
    setUploads(prev => prev.filter(u => ['uploading', 'processing', 'pending', 'paused'].includes(u.status)));
  }, []);

  const retargetFolder = useCallback((showId: string, oldPath: string, newPath: string): number => {
    if (!oldPath || oldPath === newPath) return 0;
    const prefix = `${oldPath}/`;
    let moved = 0;
    const next = uploadsRef.current.map(u => {
      if (u.showId !== showId || u.status === 'completed' || u.status === 'error') return u;
      if (u.subcategory === oldPath) {
        moved++;
        return { ...u, subcategory: newPath };
      }
      if (u.subcategory.startsWith(prefix)) {
        moved++;
        return { ...u, subcategory: `${newPath}${u.subcategory.slice(oldPath.length)}` };
      }
      return u;
    });
    if (moved > 0) {
      // Written through the ref as well as state so a worker that registers
      // before the next render still reads the new path.
      uploadsRef.current = next;
      setUploads(next);
    }
    return moved;
  }, []);

  const value = useMemo(() => ({
    uploads,
    enqueueFiles,
    retryUpload,
    removeUpload,
    pauseUpload,
    resumeUpload,
    pauseAll,
    clearQueue,
    retargetFolder,
  }), [uploads, enqueueFiles, retryUpload, removeUpload, pauseUpload, resumeUpload, pauseAll, clearQueue, retargetFolder]);

  return (
    <UploadQueueContext.Provider value={value}>
      {children}
      <UploadQueuePanel
        uploads={uploads}
        onRetry={retryUpload}
        onRemove={removeUpload}
        onPause={pauseUpload}
        onResume={resumeUpload}
        onPauseAll={pauseAll}
        onClear={clearQueue}
      />
    </UploadQueueContext.Provider>
  );
};

export const useUploadQueue = (): UploadQueueContextValue => {
  const ctx = useContext(UploadQueueContext);
  if (!ctx) {
    throw new Error('useUploadQueue must be used within an UploadQueueProvider');
  }
  return ctx;
};
